// =====================================================================
//  AMORTIZATION VIEW — operates on a static shell in the host page.
//  Host page must contain a `.amort-view` element with:
//     .amort-toggle-btn, .amort-collapsed-text, .amort-expanded
//     summary band (.amort-method-name, .amort-months, .amort-monthly, .amort-total-profit)
//     .amort-profit-label spans, .amort-month-count
//     thead variants (.amort-head-yearly, .amort-head-monthly)
//     tbody.amort-body (filled here — schedule rows are per-row dynamic)
//     tfoot variants (.amort-foot-yearly, .amort-foot-monthly) with cells
//        .amort-foot-instalment, .amort-foot-principal, .amort-foot-profit
//     footnote pair (.amort-footnote-flat, .amort-footnote-reducing) + .amort-last-month
//     buttons with [data-amort-act] for: toggle, view-yearly, view-monthly, print
//
//  Public API:
//    Calc.amortView.renderInline(container, opts)
//    Calc.amortView.openModal(opts)        // requires #amortModal in DOM
//    Calc.amortView.closeModal()
// =====================================================================
window.Calc = window.Calc || {};
Calc.amortView = (function () {
  const fmt = Calc.format;
  const fin = Calc.finance;

  function renderInline(container, opts) {
    if (!container) return;
    if (!container._amortBound) {
      bindEvents(container);
      container._amortBound = true;
    }
    if (!container._amortState) {
      container._amortState = { view: 'yearly', expanded: opts.startExpanded === true };
    }
    container._amortOpts = opts;
    update(container);
  }

  function update(container) {
    const opts = container._amortOpts;
    const state = container._amortState;
    const method = opts.method || 'flat';
    const isFlat = method === 'flat';
    const isMonthlyView = state.view === 'monthly';

    const schedule = fin.amortizationSchedule(opts.amount, opts.rate, opts.years, method);
    const yearly = fin.amortizationByYear(schedule, opts.amount);
    const totalInst = schedule.reduce((s, r) => s + r.instalment, 0);
    const totalPrin = schedule.reduce((s, r) => s + r.principal, 0);
    const totalProfit = schedule.reduce((s, r) => s + r.profit, 0);
    const monthlyInst = schedule[0] ? schedule[0].instalment : 0;

    // Summary band — text variants live in HTML, JS only toggles which is visible.
    toggleSel(container, '.amort-method-flat',     !isFlat);
    toggleSel(container, '.amort-method-reducing',  isFlat);
    setText(container, '.amort-months', schedule.length);
    setText(container, '.amort-monthly', fmt.money(monthlyInst, 2));
    setText(container, '.amort-total-profit', fmt.money(totalProfit, 2));
    setText(container, '.amort-month-count', schedule.length);
    container.querySelectorAll('.amort-profit-flat').forEach(el => el.classList.toggle('d-none', !isFlat));
    container.querySelectorAll('.amort-profit-reducing').forEach(el => el.classList.toggle('d-none', isFlat));

    // Footnotes
    container.querySelector('.amort-footnote-flat')?.classList.toggle('d-none', !isFlat);
    container.querySelector('.amort-footnote-reducing')?.classList.toggle('d-none', isFlat);
    if (isFlat) setText(container, '.amort-last-month', schedule.length);

    // Table head/foot variants
    container.querySelector('.amort-head-yearly')?.classList.toggle('d-none', isMonthlyView);
    container.querySelector('.amort-head-monthly')?.classList.toggle('d-none', !isMonthlyView);
    container.querySelector('.amort-foot-yearly')?.classList.toggle('d-none', isMonthlyView);
    container.querySelector('.amort-foot-monthly')?.classList.toggle('d-none', !isMonthlyView);

    // Foot totals (both variants kept in sync)
    container.querySelectorAll('.amort-foot-instalment').forEach(el => el.textContent = fmt.money(totalInst, 2));
    container.querySelectorAll('.amort-foot-principal').forEach(el => el.textContent = fmt.money(totalPrin, 2));
    container.querySelectorAll('.amort-foot-profit').forEach(el => el.textContent = fmt.money(totalProfit, 2));

    // Tab buttons
    container.querySelectorAll('[data-amort-act="view-yearly"]').forEach(b => b.classList.toggle('active', !isMonthlyView));
    container.querySelectorAll('[data-amort-act="view-monthly"]').forEach(b => b.classList.toggle('active', isMonthlyView));

    // Body rows — schedule data is fully dynamic.
    // Row label prefixes come from the host container's data attributes so
    // translators control the wording; JS only writes numbers.
    const yearPrefix  = container.dataset.rowYearPrefix    || '';
    const monthYrOpen  = container.dataset.rowMonthYrOpen  || ' (';
    const monthYrClose = container.dataset.rowMonthYrClose || ')';
    const tbody = container.querySelector('.amort-body');
    if (tbody) {
      tbody.innerHTML = isMonthlyView
        ? renderMonthlyRows(schedule, monthYrOpen, monthYrClose)
        : renderYearlyRows(yearly, yearPrefix);
    }

    // Expanded state
    container.querySelector('.amort-collapsed-text')?.classList.toggle('d-none', state.expanded);
    container.querySelector('.amort-expanded')?.classList.toggle('d-none', !state.expanded);
    const toggleBtn = container.querySelector('.amort-toggle-btn');
    if (toggleBtn) {
      toggleSel(toggleBtn, '.amort-toggle-show',  state.expanded);
      toggleSel(toggleBtn, '.amort-toggle-hide', !state.expanded);
      // Button colour (btn-primary) is set in the HTML markup.
    }
  }

  // Show/hide a sibling element by selector (text variants live in HTML).
  function toggleSel(root, sel, hide) {
    const el = root.querySelector(sel);
    if (el) el.classList.toggle('d-none', hide);
  }

  function renderYearlyRows(yearly, yearPrefix) {
    return yearly.map(y => `
      <tr>
        <td>${yearPrefix}${y.year}</td>
        <td>${fmt.money(y.startingBalance, 2)}</td>
        <td>${fmt.money(y.totalInstalment, 2)}</td>
        <td>${fmt.money(y.totalPrincipal, 2)}</td>
        <td>${fmt.money(y.totalProfit, 2)}</td>
        <td>${fmt.money(y.endingBalance, 2)}</td>
      </tr>
    `).join('');
  }

  function renderMonthlyRows(schedule, yrOpen, yrClose) {
    return schedule.map(r => `
      <tr${r.month % 12 === 0 ? ' style="border-bottom:2px solid var(--imoney-electric);"' : ''}>
        <td>${r.month}<span class="text-secondary fw-normal">${yrOpen}${Math.ceil(r.month / 12)}${yrClose}</span></td>
        <td class="col-instalment">${fmt.money(r.instalment, 2)}</td>
        <td>${fmt.money(r.principal, 2)}</td>
        <td>${fmt.money(r.profit, 2)}</td>
        <td>${fmt.money(r.balance, 2)}</td>
      </tr>
    `).join('');
  }

  function bindEvents(container) {
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-amort-act]');
      if (!btn) return;
      const act = btn.dataset.amortAct;
      const state = container._amortState;
      if (act === 'toggle')        { state.expanded = !state.expanded; update(container); }
      else if (act === 'view-yearly')  { state.view = 'yearly';  update(container); }
      else if (act === 'view-monthly') { state.view = 'monthly'; update(container); }
      else if (act === 'print')    { window.print(); }
    });
  }

  function setText(root, sel, val) {
    const el = root.querySelector(sel);
    if (el) el.textContent = val;
  }

  // ---------- Modal ----------
  function openModal(opts) {
    const modal = document.getElementById('amortModal');
    if (!modal) {
      console.warn('Calc.amortView.openModal: #amortModal not found on this page');
      return;
    }
    const bank = opts.bank;
    const title = opts.title || (bank ? `${bank.name} — Repayment Schedule` : 'Repayment Schedule');
    const subtitle = bank
      ? `${bank.prodname} · ${fmt.percent(opts.rate)} flat · ${opts.years} years · ${fmt.money(opts.amount, 0)}`
      : `${fmt.percent(opts.rate)} · ${opts.years} years · ${fmt.money(opts.amount, 0)}`;
    modal.querySelector('#amort-modal-title').textContent = title;
    modal.querySelector('#amort-modal-subtitle').textContent = subtitle;

    const view = modal.querySelector('.amort-view');
    if (view) {
      // Always reset to expanded yearly view each time the modal opens
      view._amortState = { view: 'yearly', expanded: true };
      renderInline(view, opts);
    }

    bootstrap.Modal.getOrCreateInstance(modal).show();
  }

  function closeModal() {
    const modal = document.getElementById('amortModal');
    if (modal) bootstrap.Modal.getOrCreateInstance(modal).hide();
  }

  return { renderInline, openModal, closeModal };
})();
